import { useEffect, useState, useMemo } from "react";
import dynamic from "next/dynamic";
import NextLink from "next/link";
import {
  Heading,
  Text,
  Box,
  Button,
  Loader,
} from "@cruk/cruk-react-components";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { TotalsSection } from "@fwa/src/components/TotalsSection";

import sharedStyles from "@fwa/src/components/styles.module.css";

import { type FundraisingPageType } from "@fwa/src/types";

const FeedListFilter = dynamic(
  () => import("@fwa/src/components/FeedListFilter"),
  { ssr: false },
);

export function FundraisingPageLatestUpdates({
  page,
  canEdit,
  feedInView,
  isStravaMetric,
  fitbitDailyTargetSteps,
  firstName,
  url,
}: {
  canEdit: boolean;
  feedInView: boolean;
  isStravaMetric: boolean;
  fitbitDailyTargetSteps: number;
  firstName: string;
  url: string | null;
  page: FundraisingPageType;
}) {
  const displayNameOrUserName =
    page.owner?.displayName !== undefined ? page.owner?.displayName : firstName;
  const { trackEventGtm } = useTracking();

  // Calculate initial feed item totals from page data
  const initialFeedItemTotals = useMemo(() => {
    if (!page)
      return {
        feeditems: 0,
        donations: 0,
        posts: 0,
        activitys: 0,
      };

    return {
      feeditems:
        0 +
        page.donationsCount +
        page.postsCount +
        (page?.milestonesCount || 0) +
        (page?.strava?.count || 0) +
        (page?.fitbit?.count || 0),
      donations: page.donationsCount,
      posts: page.postsCount,
      activitys: 0 + (page?.strava?.count || 0) + (page?.fitbit?.count || 0),
    };
  }, [page]);

  const [feedItemTotals, setFeedItemTotals] = useState(initialFeedItemTotals);

  // Update state when page changes
  useEffect(() => {
    setFeedItemTotals(initialFeedItemTotals);
  }, [initialFeedItemTotals]);

  return (
    <Box backgroundColor="background-light" padding="s" marginBottom="m">
      {page ? (
        <>
          <Heading h2 textSize="xl">
            Latest updates
          </Heading>
          {feedInView && (
            <FeedListFilter
              pageId={page.uniqueId}
              canCreate={canEdit}
              feedItemTotals={feedItemTotals}
              setFeedItemTotals={setFeedItemTotals}
              pageUrl={url}
              isStravaMetric={isStravaMetric}
              fitbitDailyTargetSteps={fitbitDailyTargetSteps}
              allowedFilters={["feeditems", "posts", "donations", "activitys"]}
            />
          )}
          {!page.donationsCount && page.pageStatus !== "closed" && (
            <Box marginTop="s" data-component="latest-updates">
              <div className={sharedStyles["border"]}>
                <Text textAlign="center">{`${displayNameOrUserName} needs your support. Be the first to make a donation.`}</Text>
                <NextLink
                  href={`/donate/${page.uniqueId}/details`}
                  onClick={() => {
                    trackEventGtm({
                      event: "donate_cta",
                      cta: "first donation",
                    });
                  }}
                  data-cta-type="donate"
                >
                  <Button appearance="primary" full as="span">
                    Donate
                  </Button>
                </NextLink>
              </div>
            </Box>
          )}
          <TotalsSection
            donationsTotalAmount={page.donationsTotalAmount}
            donationsTotalAmountFake={page.donationsTotalAmountFake}
            donationsTotalAmountFacebook={page.donationsTotalAmountFacebook}
          />
        </>
      ) : (
        <Loader />
      )}
    </Box>
  );
}

export default FundraisingPageLatestUpdates;
